import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Calendar, TrendingDown, TrendingUp, Award, Sparkles } from 'lucide-react';
import { getCheckinDate, getRTIState } from '../lib/checkin-utils';

interface RetrospectiveData {
  hasData: boolean;
  oneMonthAgo?: {
    date: string;
    rtiScore: number;
    rtiState: string;
    emotionalState: string;
    action: string;
  };
  today: {
    date: string;
    rtiScore: number;
    rtiState: string;
    emotionalState: string;
    action: string;
  };
  improvement: number;
  improvementPercent: number;
}

export function MonthlyRetrospective() {
  const [data, setData] = useState<RetrospectiveData | null>(null);

  useEffect(() => {
    calculateRetrospective();
  }, []);

  const calculateRetrospective = () => {
    const activeRelationshipId = localStorage.getItem('tension_active_relationship');
    const allCheckins = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    
    // Filter for active relationship
    const checkins = activeRelationshipId
      ? allCheckins.filter((c: any) => c.relationshipId === activeRelationshipId)
      : allCheckins;

    if (checkins.length < 2) {
      return;
    }

    // Sort by timestamp
    const sorted = checkins.sort((a: any, b: any) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    // Get today's most recent check-in
    const today = sorted[sorted.length - 1];

    // Find check-in closest to 30 days ago
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    let closestCheckin = null;
    let minDiff = Infinity;

    for (const checkin of sorted) {
      const checkinDate = new Date(getCheckinDate(checkin));
      const diff = Math.abs(checkinDate.getTime() - thirtyDaysAgo.getTime());
      if (diff < minDiff) {
        minDiff = diff;
        closestCheckin = checkin;
      }
    }

    // Only show if we have data at least 20 days apart
    const daysDiff = Math.floor((new Date(getCheckinDate(today)).getTime() - new Date(getCheckinDate(closestCheckin)).getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff < 20 || !closestCheckin) {
      return;
    }

    const getEmotionalStateLabel = (emotionalAverage: number) => {
      if (!emotionalAverage) return 'Not tracked';
      if (emotionalAverage <= 1.5) return 'Anxious';
      if (emotionalAverage <= 2.5) return 'Uncertain';
      if (emotionalAverage <= 3.5) return 'Neutral';
      if (emotionalAverage <= 4.5) return 'Hopeful';
      return 'Connected';
    };

    // getRTIState is now imported from checkin-utils

    const getAction = (score: number) => {
      if (score <= 35) return 'Maintain momentum';
      if (score <= 60) return 'Light reconnection';
      return 'Practice restraint';
    };

    const oldScore = closestCheckin.rtiScore || 0;
    const newScore = today.rtiScore || 0;
    const improvement = oldScore - newScore;
    const improvementPercent = oldScore > 0 ? Math.round((improvement / oldScore) * 100) : 0;

    setData({
      hasData: true,
      oneMonthAgo: {
        date: new Date(getCheckinDate(closestCheckin)).toLocaleDateString(),
        rtiScore: oldScore,
        rtiState: getRTIState(oldScore),
        emotionalState: getEmotionalStateLabel(closestCheckin.emotionalAverage),
        action: getAction(oldScore),
      },
      today: {
        date: new Date(getCheckinDate(today)).toLocaleDateString(),
        rtiScore: newScore,
        rtiState: getRTIState(newScore),
        emotionalState: getEmotionalStateLabel(today.emotionalAverage),
        action: getAction(newScore),
      },
      improvement,
      improvementPercent,
    });
  };

  if (!data?.hasData) {
    return null;
  }

  const isImprovement = data.improvement > 0;
  const isSignificant = Math.abs(data.improvement) >= 10;

  return (
    <Card className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200">
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-indigo-600" />
          <h3 className="font-medium text-neutral-900">Monthly Progress</h3>
        </div>

        {/* Comparison */}
        <div className="grid grid-cols-2 gap-4">
          {/* One Month Ago */}
          <div className="bg-white/60 rounded-lg p-4">
            <p className="text-xs text-neutral-500 mb-2">📅 {data.oneMonthAgo?.date}</p>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-neutral-600">RTI Score</p>
                <p className="text-2xl font-light text-neutral-900">{data.oneMonthAgo?.rtiScore}</p>
                <p className={`text-xs ${
                  data.oneMonthAgo?.rtiState === 'Stable' ? 'text-green-600' :
                  data.oneMonthAgo?.rtiState === 'Elastic' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {data.oneMonthAgo?.rtiState}
                </p>
              </div>
              <div>
                <p className="text-xs text-neutral-600">Emotional State</p>
                <p className="text-sm font-medium text-neutral-900">{data.oneMonthAgo?.emotionalState}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-600">Action</p>
                <p className="text-sm text-neutral-700">{data.oneMonthAgo?.action}</p>
              </div>
            </div>
          </div>

          {/* Today */}
          <div className="bg-white/60 rounded-lg p-4 ring-2 ring-indigo-200">
            <p className="text-xs text-neutral-500 mb-2">📅 {data.today.date}</p>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-neutral-600">RTI Score</p>
                <p className="text-2xl font-light text-neutral-900">{data.today.rtiScore}</p>
                <p className={`text-xs ${
                  data.today.rtiState === 'Stable' ? 'text-green-600' :
                  data.today.rtiState === 'Elastic' ? 'text-yellow-600' :
                  'text-red-600'
                }`}>
                  {data.today.rtiState}
                </p>
              </div>
              <div>
                <p className="text-xs text-neutral-600">Emotional State</p>
                <p className="text-sm font-medium text-neutral-900">{data.today.emotionalState}</p>
              </div>
              <div>
                <p className="text-xs text-neutral-600">Action</p>
                <p className="text-sm text-neutral-700">{data.today.action}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Change Summary */}
        <div className={`rounded-lg p-4 ${
          isImprovement 
            ? 'bg-green-50 border border-green-200' 
            : data.improvement < 0
            ? 'bg-red-50 border border-red-200'
            : 'bg-neutral-50 border border-neutral-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {isImprovement ? (
              <TrendingDown className="w-5 h-5 text-green-600" />
            ) : data.improvement < 0 ? (
              <TrendingUp className="w-5 h-5 text-red-600" />
            ) : (
              <Award className="w-5 h-5 text-neutral-600" />
            )}
            <p className={`font-medium ${
              isImprovement ? 'text-green-900' : 
              data.improvement < 0 ? 'text-red-900' : 
              'text-neutral-900'
            }`}>
              {isImprovement 
                ? `✨ You reduced tension by ${data.improvement} points` 
                : data.improvement < 0
                ? `Tension increased by ${Math.abs(data.improvement)} points`
                : 'RTI remained stable'}
            </p>
          </div>
          <p className={`text-sm ${
            isImprovement ? 'text-green-700' : 
            data.improvement < 0 ? 'text-red-700' : 
            'text-neutral-700'
          }`}>
            {isImprovement && isSignificant && 'Your restraint and awareness strategies are working. Keep it up!'}
            {isImprovement && !isSignificant && 'Slight improvement. Stay consistent with your approach.'}
            {data.improvement < 0 && isSignificant && 'Tension has increased. Review your action cards and consider strategic restraint.'}
            {data.improvement < 0 && !isSignificant && 'Minor increase. Monitor patterns closely.'}
            {data.improvement === 0 && 'Consistency is key. Continue your current strategies.'}
          </p>
        </div>

        {/* Insights */}
        {isImprovement && isSignificant && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-purple-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-neutral-900">Major Progress!</p>
                <p className="text-xs text-neutral-700 mt-1">
                  A {Math.abs(data.improvementPercent)}% reduction in tension over 30 days shows your self-awareness 
                  and behavioral changes are creating real impact. This is the power of strategic relationship management.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
